import React, { FC, useContext, useEffect, useState } from "react";
import { Table, Empty } from "antd";
import type { TablePaginationConfig } from "antd/es/table";
import GlobalContext from "src/context/global-context";
import useAuth from "src/hooks/use-auth";
import { useIsMounted } from "src/lib/hooks/use-is-mounted";
import { getErrorMessage, logger } from "src/lib/utils";
import { useMutation } from "react-query";
import classNames from "classnames";
import client from "../../api";
import {
  APISomethingWrongMsg,
  LIST_PAGE_SIZE,
  NetworkDisconnectMsg,
  permissions as permissionConstants,
} from "src/lib/constants";
import usePermissions from "src/hooks/use-permissions";
// ─── Types ──────────────────────────────────────────────────────────────────
interface IExportFilters {
  from?: string;
  to?: string;
  status?: string;
  currency?: string;
  payment_system?: string;
}

interface IExportItem {
  key: string;
  id: number;
  type: string;
  filters: IExportFilters;
}

interface IExportListParams {
  page: number;
  perPage: number;
}

interface IExportListResponse {
  status: string;
  code: number;
  message: string;
  data: Record<string, unknown>;
  items: {
    id: number;
    type: string;
    filters: IExportFilters;
  }[];
  totalcount: number;
  page: number;
}

interface IDownloadResponse {
  status: string;
  code: number;
  message: string;
  data: string; // download URL
}

// ─── Icons ──────────────────────────────────────────────────────────────────

const ExcelIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="16" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const PdfIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <path d="M9 15v-2h2a1 1 0 0 1 0 2H9z" />
    <path d="M9 18v-1" />
    <path d="M13 13v5" />
    <path d="M13 13h1.5a1.5 1.5 0 0 1 0 3H13" />
  </svg>
);

const DownloadIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 3v12" />
    <path d="M7 10l5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);

const SpinnerIcon = () => (
  <svg
    className="animate-spin w-4 h-4 text-secondary"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

// ─── Type Badge ─────────────────────────────────────────────────────────────
const typeBadgeConfig: Record<
  string,
  { bg: string; text: string; icon: React.ReactNode; label: string }
> = {
  excel: {
    bg: "bg-green-50",
    text: "text-green-700",
    icon: <ExcelIcon />,
    label: "Excel",
  },
  pdf: {
    bg: "bg-red-50",
    text: "text-red-700",
    icon: <PdfIcon />,
    label: "PDF",
  },
};

const ExportTypeBadge: FC<{ type: string }> = ({ type }) => {
  const cfg = typeBadgeConfig[type] ?? {
    bg: "bg-gray-100",
    text: "text-gray-600",
    icon: null,
    label: type,
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-[6px] text-[12px] font-semibold uppercase tracking-wide",
        cfg.bg,
        cfg.text,
      )}
    >
      {cfg.icon}
      {cfg.label}
    </span>
  );
};

const formatLabel = (value: string) =>
  value.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// ─── Main Component ───────────────────────────────────────────────────────────

const ExportsHistory: FC = () => {
  const { setIsAlertOpened, setAlertText, setAlertTitle } =
    useContext(GlobalContext);
  const isMounted = useIsMounted();
  const { isAuthorized } = useAuth();

  const targetModule = permissionConstants.EXPORT_HISTORY;
  const { canView, canList, canExport, canDownload } = usePermissions([
    targetModule
  ]);
  const canViewExports = canView || canList;
  const canDownloadExports = canDownload || canExport;

  // ── Pagination ──
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);

  // ── Data ──
  const [dataSource, setDataSource] = useState<IExportItem[]>([]);

  // ── Track which row is currently downloading ──
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  // ── Error handler (shared) ──
  const handleApiError = (error: any) => {
    if (error.code === "ERR_NETWORK") {
      setAlertText(NetworkDisconnectMsg);
    } else {
      if (error.response) {
        setAlertText(getErrorMessage(error.response.data));
      } else {
        setAlertText(APISomethingWrongMsg);
      }
    }
    setIsAlertOpened(true);
  };

  // ── List Mutation ──
  const { mutate: getExportsList, isLoading: isExportsListLoading } =
    useMutation<IExportListResponse, any, IExportListParams>(
      client.newTransactionsHistory.ExportHistoryList,
      {
        onSuccess: (data) => {
          logger.log("getExportsList response:", data);
          setTotal(data?.totalcount ?? 0);
          setDataSource(
            (data?.items ?? []).map((item) => ({
              key: String(item.id),
              id: item.id,
              type: item.type,
              filters: item.filters ?? {},
            })),
          );
        },
        onError: handleApiError,
      },
    );

  const { mutate: downloadExportItem } = useMutation(
    client.newTransactionsHistory.DownloadFromExportHistoryList,
    {
      onSuccess: (response: any, variables: any) => {
        logger.log("DownloadExportHistoryItem response:", response);

        const fileUrl = response?.data;
        if (fileUrl) {
          const link = document.createElement("a");
          link.href = fileUrl;
          link.download = "";
          // link.download = `export${variables.id}`;

          link.target = "_blank";
          link.rel = "noopener noreferrer";
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
        setDownloadingId(null);
      },
      onError: (error: any) => {
        setDownloadingId(null);
        handleApiError(error);
      },
    },
  );

  const buildParams = (page: number): IExportListParams => ({
    page,
    perPage: LIST_PAGE_SIZE,
  });

  useEffect(() => {
    if (!isMounted || !isAuthorized || !canViewExports) return;
    getExportsList(buildParams(currentPage));
  }, [currentPage, isMounted, isAuthorized, canViewExports]);

  const handleTableChange = (pagination: TablePaginationConfig) => {
    if (!canViewExports) return;
    const page = pagination.current ?? 1;
    setCurrentPage(page);
    getExportsList(buildParams(page));
  };

  const handleDownload = (id: number) => {
    if (!canDownloadExports) {
      if (setAlertTitle) setAlertTitle("Error");
      setAlertText("You do not have permission to download exports");
      setIsAlertOpened(true);
      return;
    }
    setDownloadingId(id);
    downloadExportItem(id);
  };

  // ── Ant Design Table Columns ──
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 100,
      render: (val: number) => (
        <span className="text-primary text-[13px] font-medium">#{val}</span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 140,
      render: (val: string) => <ExportTypeBadge type={val} />,
    },
    {
      title: "Filters",
      dataIndex: "filters",
      key: "filters",
      width: 350,
      render: (filters: IExportFilters) => {
        if (!filters || Object.keys(filters).length === 0) {
          return "—";
        }

        return (
          <div className="flex flex-wrap gap-1">
            {Object.entries(filters).map(([key, value]) => (
              <span
                key={key}
                className="px-2 py-1 rounded bg-gray-100 text-[12px]"
              >
                <strong>{formatLabel(key)}:</strong> {value}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 140,
      render: (_: unknown, record: IExportItem) => {
        if (!canDownloadExports) {
          return <span className="text-secondary opacity-50">—</span>;
        }
        const isDownloading = downloadingId === record.id;
        return (
          <button
            type="button"
            onClick={() => handleDownload(record.id)}
            disabled={isDownloading}
            className={classNames(
              "flex items-center gap-2 h-[36px] px-3 rounded-[8px] text-[13px] font-medium border transition-colors",
              isDownloading
                ? "opacity-50 cursor-not-allowed bg-white border-[#E6E8EC] text-secondary"
                : "bg-white border-[#E6E8EC] text-primary hover:border-brand hover:text-brand shadow-sm",
            )}
          >
            {isDownloading ? <SpinnerIcon /> : <DownloadIcon />}
            Download
          </button>
        );
      },
    },
  ];

  return (
    <div className="w-full h-full bg-gray">
      <div className="w-full h-full flex flex-col py-2 px-4 sm:p-4 sm:pr-11 gap-4">
        {/* ── Page Title ── */}
        <p className="text-[25px] sm:text-[32px] font-manrope text-primary font-medium leading-[37.5px] sm:leading-[48px]">
          Exports List
        </p>

        {!canViewExports ? (
          <div className="flex flex-col items-center justify-center bg-white rounded-[12px] p-8 text-center shadow-sm border border-[#E6E8EC]">
            <p className="font-manrope text-[16px] font-semibold text-secondary">
              You do not have permission to view exports history.
            </p>
          </div>
        ) : (
          /* ── Table Card ── */
          <div className="flex flex-col mt-1 bg-white rounded-[12px] shadow-sm border border-[#E6E8EC] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#F0F2F5]">
              <span className="text-[13px] text-secondary font-medium">
                {total} export{total !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="overflow-x-auto">
              <Table<IExportItem>
                dataSource={dataSource}
                columns={columns}
                loading={isExportsListLoading}
                rowKey="key"
                scroll={{ x: 500 }}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        <span className="text-[15px] text-secondary font-medium">
                          No exports found
                        </span>
                      }
                    />
                  ),
                }}
                pagination={{
                  current: currentPage,
                  pageSize: LIST_PAGE_SIZE,
                  total,
                  showSizeChanger: false,
                  showTotal: (tot, range) =>
                    `${range[0]}–${range[1]} of ${tot} exports`,
                  className: "px-5 py-3",
                }}
                onChange={handleTableChange}
                className="exports-history-table"
                rowClassName="hover:bg-[#FAFBFC] transition-colors"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExportsHistory;
